/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import dynamic from 'next/dynamic';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {GenericObject} from '@ion/core/src/types/entities';
import {useSavedMapViews} from '@ion/dls/src/maps/hooks/useSavedMapViews';
import {MapContainerProps} from '@ion/dls/src/maps/types/types';
import {SideSheetView} from '@ion/dls/src/types/types';
import {useSaveAssetViewModeSetter} from '@/routes/tracking/hooks/sidesheet/useSaveAssetViewMode';
import {setSideSheetStatus} from '@/store/actions/nav';
import {
  setFindNearbyFromItem,
  setFindNearbyPanelActive,
  setMapOnlyView,
  setMapWidgetSelected
} from '@/store/actions/tracking';
import {
  fleetLayerPersistenceActiveSelector,
  isMapOnlyViewSelector,
  isSelectedFiltersActiveSelector,
  liveHistoryPersistActiveSelector,
  mapWidgetSelectedSelector,
  savedMapViewsSelector,
  sideSheetViewSelector
} from '@/store/selectors/tracking-selectors';
import {getFindNearbyLocation} from '../../../helpers';
import useMapToolProps from '../hooks/useMapToolProps';

const DynamicMapToolContainer = dynamic(() => import('@ion/dls/src/maps/toolbar/MapToolContainer'), {
  ssr : false
});

interface MapWidgetContainerProps extends MapContainerProps {
  onZoomToFit?: Function;
  editGeofenceGraphic?: any;
  setEditGeofenceGraphic?: Function;
  canAddGeofence?: boolean;
  renderMapWidgets?: boolean;
  isAssetHistoryReport?: boolean;
  mapWidgetProps?: GenericObject;
};

const MapWidgetContainer = ({
  map,
  view,
  onZoomToFit,
  canAddGeofence,
  mapWidgetProps,
  editGeofenceGraphic,
  setEditGeofenceGraphic,
  renderMapWidgets = true,
  isAssetHistoryReport = false
}: MapWidgetContainerProps) => {
  const dispatch                    = useDispatch();
  const mapToolProps                = useMapToolProps();
  const isMounted                   = useHasMounted(null);
  const getDefaultMapView           = useSavedMapViews();
  const mapRef                      = useRef(map);
  const mapViewRef                  = useRef(view);
  const savedMapViews               = useSelector(savedMapViewsSelector);
  const mapWidgetSelected           = useSelector(mapWidgetSelectedSelector);
  const hasTrackingFilterActive     = useSelector(isSelectedFiltersActiveSelector);
  const liveHistoryPersistActive    = useSelector(liveHistoryPersistActiveSelector);
  const fleetLayerPersistenceActive = useSelector(fleetLayerPersistenceActiveSelector);
  const sideSheetView               = useSelector(sideSheetViewSelector);
  const isMapOnlyView               = useSelector(isMapOnlyViewSelector);
  const setViewMode                 = useSaveAssetViewModeSetter();

  const {createOSMBasemap, setSavedMapView} = mapToolProps?.bottomSheetProps || {};
  const {updateMapLayers}   = mapToolProps?.layerChooserProps || {};
  const defaultSavedMapView = useMemo(() => getDefaultMapView(savedMapViews), [savedMapViews]);

  const setFindNearbyActions = useCallback(location => {
    dispatch(setFindNearbyFromItem(getFindNearbyLocation(location)));
    dispatch(setFindNearbyPanelActive(true));
  }, []);

  const onMapWidgetSelected = useCallback(selected => {
    dispatch(setMapWidgetSelected(selected));
  }, []);

  // routes through useSaveAssetViewMode's own setViewMode (rather than
  // dispatching setSideSheetView directly) so the Page Layout widget's
  // choice also updates the persisted view-mode preference - otherwise
  // SidesheetBreadcrumbs' saveViewMode() (called on every breadcrumb/back
  // navigation) re-applies whatever stale preference value was last
  // written there, silently overwriting the layout the user just picked...
  const onSetSideSheetView = useCallback((id: SideSheetView) => {
    setViewMode(id);
  }, [setViewMode]);

  const onSetMapOnlyView = useCallback((mapOnly: boolean) => {
    dispatch(setMapOnlyView(mapOnly));
  }, []);

  // the desktop side sheet's own open/closed drawer state is tracked
  // separately from sideSheetView (which variant to show once open) -
  // mirrors the same explicit open/close the old SidesheetModeMenu's
  // onView handler already did (useOnView.ts), since simply changing
  // sideSheetView does not by itself open a currently-closed sheet...
  const onSetDesktopSideSheetOpen = useCallback((open: boolean) => {
    dispatch(setSideSheetStatus({desktopSideSheetOpen: open}));
  }, []);

  const setMapLayers = useCallback(() => {
    if (defaultSavedMapView) {
      const {operationalLayers, layers} = defaultSavedMapView;
      updateMapLayers?.({
        savedOperationalLayers : operationalLayers,
        savedLayers            : layers,
        isAppendLayers         : false
      });
    }
  }, [defaultSavedMapView]);

  useEffect(function() {
    mapRef.current = map;
    setMapLayers();
  }, [map]);

  useEffect(function() {
    mapViewRef.current = view;
  }, [view]);

  const onSavedMapView = useCallback((savedMapView, applyExtent) => {
    setSavedMapView(mapViewRef.current, savedMapView, applyExtent);
  }, []);

  const onGoToHomeExtent = useCallback(() => {
    onZoomToFit?.();
  }, []);

  const mapWidgetsProps = useMemo(() => {
    return {
      ...mapToolProps,
      ...mapWidgetProps
    };
  }, [mapToolProps, mapWidgetProps]);

  if (isMounted) {
    return (
      <DynamicMapToolContainer map={map}
        view={view}
        createOSMBasemap={createOSMBasemap}
        setSavedMapView={onSavedMapView}
        onGoToHomeView={onGoToHomeExtent}
        editGeofenceGraphic={editGeofenceGraphic}
        setEditGeofenceGraphic={setEditGeofenceGraphic}
        canAddGeofence={canAddGeofence}
        setFindNearbyActions={setFindNearbyActions}
        setMapWidgetSelected={onMapWidgetSelected}
        mapWidgetSelected={mapWidgetSelected}
        renderMapWidgets={renderMapWidgets}
        hasTrackingFilterActive={hasTrackingFilterActive}
        liveHistoryPersistActive={liveHistoryPersistActive}
        fleetLayerPersistenceActive={fleetLayerPersistenceActive}
        isAssetHistoryReport={isAssetHistoryReport}
        sideSheetView={sideSheetView}
        isMapOnlyView={isMapOnlyView}
        onSetSideSheetView={onSetSideSheetView}
        onSetMapOnlyView={onSetMapOnlyView}
        onSetDesktopSideSheetOpen={onSetDesktopSideSheetOpen}
        styles={{
          moreVerticalTool : {
            marginTop : '0'
          }
        }}
        {...mapWidgetsProps} />
    );
  }
};

export default MapWidgetContainer;
